import { CompanyIcon, CustomerIcon, DeleteIcon, FormEditIcon, OutlinedInfoIcon } from 'assets/icons';
import React, { useState } from 'react';
import { Assignee, JobExpense } from '../../jobs.model';
import { CustomButton, GenericDeleteModal, ImagePreview, InfiniteLoadingSpinner } from 'components';
import { ButtonType } from 'components/custom-button/CustomButton';
import LineItemPlusReactIcon from 'assets/react-icons/LineItemPlusReactIcon';
import AddExpenseModal from './AddExpenseModal';
import {
  useCreateJobExpenseMutation,
  useDeleteJobExpenseMutation,
  useEditJobExpenseMutation,
  useUpdateJobExpenseReceiptMutation
} from 'containers/jobs/api';
import {
  selectCurrentTeam,
  selectCurrentTeamAccountingPermissions,
  selectCurrentTeamJobPermissions,
  selectUserDetails
} from 'containers/app/appSlice';
import { useAppSelector } from 'hooks';
import { CreateJobExpenseRequest } from '../../jobs.model';
import { getAmountWithCurrency } from 'utils/amountUtils';
import DateUtils from 'utils/dateUtils';
import { TooltipWrapper } from 'components/tooltip/TooltipWrapper';
import { JobExpensesSummary } from '../hooks/usePaginatedJobExpensesQuery';
import { JobStatus } from 'containers/jobs/constant';

const tableHeader = [
  {
    label: 'Category',
    key: 'category'
  },
  {
    label: 'Expense',
    key: 'expense'
  },
  {
    label: 'Reimburse to',
    key: 'reimburseTo'
  },
  {
    label: 'Receipt',
    key: 'receipt'
  },
  {
    label: 'Date',
    key: 'date'
  },
  {
    label: 'Amount',
    key: 'amount'
  },
  {
    label: '',
    key: 'actions'
  }
];

interface ExpensesTableProps {
  jobId: string;
  expenses: JobExpense[];
  loadMore: () => void;
  hasMore: boolean;
  isFetching: boolean;
  summary: JobExpensesSummary;
  resetRecallQuery: () => void;
  jobStatus: JobStatus;
  hasPermissionsToView: boolean;
  isJobEditable: boolean;
  jobMembers: Assignee[];
}

const ExpensesTable: React.FC<ExpensesTableProps> = ({
  jobId,
  expenses,
  hasMore,
  isFetching,
  summary,
  resetRecallQuery,
  jobStatus,
  hasPermissionsToView,
  isJobEditable,
  jobMembers
}) => {
  const currentUser = useAppSelector(selectUserDetails);
  const currentTeam = useAppSelector(selectCurrentTeam);
  const currentTeamAccountingPermissions = useAppSelector(selectCurrentTeamAccountingPermissions);
  const { createExpenses, manageOthersExpenses } = currentTeamAccountingPermissions ?? {};
  const currentTeamJobPermissions = useAppSelector(selectCurrentTeamJobPermissions);
  const { modifyDoneJobs } = currentTeamJobPermissions ?? {};

  const [isExpenseModalOpen, setIsExpenseModalOpen] = useState({ isEdit: false, isOpen: false });
  const [selectedExpense, setSelectedExpense] = useState<JobExpense | null>(null);
  const [isImagePreviewOpen, setIsImagePreviewOpen] = useState(false);
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false);

  const [createJobExpense] = useCreateJobExpenseMutation();
  const [editJobExpense] = useEditJobExpenseMutation();
  const [deleteJobExpense, { isLoading: isDeletingExpense }] = useDeleteJobExpenseMutation();
  const [updateJobExpenseReceipt, { isLoading: isEditingReceipt }] = useUpdateJobExpenseReceiptMutation();

  // const expensesListLoader = useRef(loadMore);

  // const { setIntersectionElement } = useIntersectionObserver({
  //   intersectionCallBack: expensesListLoader,
  //   threshold: 0.5
  // });

  // useEffect(() => {
  //   expensesListLoader.current = loadMore;
  // }, [loadMore]);

  const handleCreateExpense = async (expenseData: CreateJobExpenseRequest) => {
    if (!isJobEditable) {
      return;
    }
    if (isExpenseModalOpen.isEdit && selectedExpense) {
      try {
        await editJobExpense({
          teamId: currentTeam.id,
          jobId,
          expenseId: selectedExpense.id,
          body: expenseData
        }).unwrap();

        setIsExpenseModalOpen({ isEdit: false, isOpen: false });
        resetRecallQuery(); // Refresh the expenses list
      } catch (error) {
        // Error handling is done by RTK Query
      }
    } else {
      try {
        await createJobExpense({
          teamId: currentTeam.id,
          jobId,
          body: expenseData
        }).unwrap();

        setIsExpenseModalOpen({ isEdit: false, isOpen: false });
        resetRecallQuery(); // Refresh the expenses list
      } catch (error) {
        // Error handling is done by RTK Query
      }
    }
  };

  const handleEditReceipt = async (receiptUrl: string) => {
    try {
      await updateJobExpenseReceipt({
        teamId: currentTeam.id,
        jobId,
        expenseId: selectedExpense?.id,
        body: {
          receiptUrl
        }
      }).unwrap();
    } catch (error) {
      // Error handling is done by RTK Query
    }
  };

  const handleDeleteExpense = async (expense: JobExpense) => {
    try {
      await deleteJobExpense({
        teamId: currentTeam.id,
        jobId,
        expenseId: expense.id
      }).unwrap();
      resetRecallQuery(); // Refresh the expenses list
      setIsDeleteConfirmationOpen(false);
    } catch (error) {
      // Error handling is done by RTK Query
    }
  };

  const handleOpenExpenseModal = () => {
    setIsExpenseModalOpen({ isEdit: false, isOpen: true });
  };

  const handleCloseExpenseModal = () => {
    setIsExpenseModalOpen({ isEdit: false, isOpen: false });
  };

  const handleEditExpense = (expense: JobExpense) => {
    setIsExpenseModalOpen({ isEdit: true, isOpen: true });
    setSelectedExpense(expense);
  };

  const handleViewReceipt = (expense: JobExpense) => {
    setIsImagePreviewOpen(true);
    setSelectedExpense(expense);
  };

  const handleDeleteConfirmation = (expense: JobExpense) => {
    setIsDeleteConfirmationOpen(true);
    setSelectedExpense(expense);
  };

  const handleCloseDeleteConfirmation = () => {
    setIsDeleteConfirmationOpen(false);
  };

  const canEditExpense = (expense: JobExpense) => {
    if (!isJobEditable) {
      return false;
    }
    if (jobStatus === JobStatus.DONE) {
      return modifyDoneJobs;
    }
    if (currentUser?.id !== expense.createdBy) {
      return manageOthersExpenses;
    }
    return createExpenses;
  };

  const canAddExpense = () => {
    if (!isJobEditable) {
      return false;
    }
    if (jobStatus === JobStatus.DONE) {
      return modifyDoneJobs;
    }
    return createExpenses;
  };

  return (
    <>
      <div className="w-full overflow-hidden rounded-md rounded-br-none border border-borderGray">
        <table className="w-full border-collapse bg-white">
          {expenses.length > 0 && (
            <thead className="border-b border-borderGray bg-[#F8F8F8]">
              <tr className="text-left text-sbase font-semibold text-accentText">
                {tableHeader.map(header => (
                  <th key={header.key} className="p-4">
                    {header.label}
                  </th>
                ))}
              </tr>
            </thead>
          )}
          <tbody className="divide-y divide-secondaryBtn">
            {expenses.length === 0 && hasPermissionsToView ? (
              <tr>
                <td
                  colSpan={tableHeader.length}
                  className="px-4 py-2 text-center text-sbase font-bold text-primaryText">
                  No expenses found
                </td>
              </tr>
            ) : (
              expenses.map(expense => (
                <tr key={expense.id} className="[&>*]:px-4 [&>*]:py-2.5">
                  <td className="align-top text-sbase font-semibold text-primaryText">{expense.category}</td>
                  <td className="align-top">
                    <div className="flex max-w-[200px] flex-col gap-1">
                      <span className="text-sbase font-bold text-primaryText">{expense.title}</span>
                      <span className="max-w-[98%] whitespace-pre-wrap text-px13 text-accentText line-clamp-2">
                        {expense.description}
                      </span>
                    </div>
                  </td>
                  <td className="align-top">
                    <div className="flex flex-col gap-2">
                      <span className="text-sbase font-semibold text-primaryText">
                        {expense.reimburseToMerchantName || '-'}
                      </span>
                      <span
                        className="inline-flex w-fit items-center gap-1 rounded-md
                     border border-gray-1 px-2.5 py-0.5 text-px13 font-medium text-accentText">
                        {expense.type === 'TEAM' ? (
                          <>
                            <CustomerIcon className="path-fill-current h-3.5 w-3.5 text-accentText" />
                            <span className="text-accentText">Team</span>
                          </>
                        ) : (
                          <>
                            <CompanyIcon className="path-fill-current h-3.5 w-3.5 text-accentText" />
                            <span className="text-accentText">Company</span>
                          </>
                        )}
                      </span>
                    </div>
                  </td>
                  <td className="align-top">
                    {expense.receiptUrl ? (
                      <button
                        className="text-sbase font-semibold text-secondary hover:text-secondary"
                        onClick={() => handleViewReceipt(expense)}>
                        View
                      </button>
                    ) : (
                      <button
                        className="text-sbase font-semibold text-secondary hover:text-secondary"
                        onClick={() => handleEditExpense(expense)}>
                        + Add
                      </button>
                    )}
                  </td>
                  <td className="align-top text-sbase font-semibold text-primaryText">
                    {DateUtils.getDateInFormat({ date: new Date(expense.expenseDate), dateFormat: 'MMM d, yyyy' })}
                  </td>
                  <td className="align-top text-sbase font-bold text-primaryText">
                    {getAmountWithCurrency(expense.amount, '$')}
                  </td>
                  <td className="flex space-x-3 align-top">
                    {canEditExpense(expense) && (
                      <>
                        <button
                          onClick={() => handleEditExpense(expense)}
                          className="p-1.5 transition-opacity hover:opacity-80"
                          aria-label="Edit expense">
                          <FormEditIcon className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleDeleteConfirmation(expense)}
                          className="p-1.5 transition-opacity hover:opacity-80"
                          aria-label="Delete expense">
                          <DeleteIcon className="path-fill-current h-5 w-5 text-accentText" />
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))
            )}
            <tr>
              {/* Intersection Observer Target */}
              {hasMore && (
                <td colSpan={tableHeader.length}>
                  {/* <div ref={setIntersectionElement} className="flex items-center justify-center p-4"> */}
                  {isFetching ? (
                    <InfiniteLoadingSpinner />
                  ) : (
                    <div className="h-10 w-full" /> // Spacer for intersection observer
                  )}
                  {/* </div> */}
                </td>
              )}
            </tr>
            {canAddExpense() && (
              <tr>
                <td colSpan={tableHeader.length} className="px-3">
                  <div className="flex">
                    <CustomButton
                      className="p-0"
                      id="add-expense-button"
                      type={ButtonType.GHOST}
                      onClick={handleOpenExpenseModal}
                      StartIcon={LineItemPlusReactIcon}>
                      Add expense
                    </CustomButton>
                  </div>
                </td>
              </tr>
            )}
            {!hasPermissionsToView && (
              <tr>
                <td colSpan={tableHeader.length} className="p-4">
                  <div className="flex">
                    <div className="text-sbase font-semibold text-primaryText">No permissions to view expenses</div>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {/* Summary Section */}
      {!isFetching && hasPermissionsToView && expenses?.length > 0 && (
        <div className="ml-auto w-1/2 rounded-b-md border border-t-0 border-borderGray">
          <div className="flex flex-col">
            <div className="flex w-4/5 justify-between py-4 pl-3 pr-10">
              <span className="flex items-center gap-2 text-17px font-semibold text-heading">
                Company expenses
                <TooltipWrapper tooltip="Non reimbursable business related expenses.">
                  <OutlinedInfoIcon className="path-fill-current h-4 w-4 text-accentText" />
                </TooltipWrapper>
              </span>
              <span className="text-17px font-semibold text-heading">
                {getAmountWithCurrency(summary?.companyExpenses || 0, '$')}
              </span>
            </div>
            <div className="flex w-4/5 justify-between pl-3 pr-10 pb-4">
              <span className="flex items-center gap-2 text-17px font-semibold text-heading">
                Team expenses
                <TooltipWrapper tooltip="Job related expenses that are reimbursable to members.">
                  <OutlinedInfoIcon className="path-fill-current h-4 w-4 text-accentText" />
                </TooltipWrapper>
              </span>
              <span className="text-17px font-semibold text-heading">
                {getAmountWithCurrency(summary?.teamExpenses || 0, '$')}
              </span>
            </div>
            <div className="border-t"></div>
            <div className="flex w-4/5 justify-between py-4 pl-3 pr-10">
              <span className="text-17px font-bold text-heading">Total</span>
              <span className="text-17px font-bold text-heading">
                {getAmountWithCurrency(summary?.total || 0, '$')}
              </span>
            </div>
          </div>
        </div>
      )}

      {isExpenseModalOpen.isOpen && (
        <AddExpenseModal
          open={isExpenseModalOpen.isOpen}
          onClose={handleCloseExpenseModal}
          onSave={handleCreateExpense}
          membersList={jobMembers || []}
          isEdit={isExpenseModalOpen.isEdit}
          selectedExpense={selectedExpense}
          jobId={jobId}
          handleEditReceipt={handleEditReceipt}
          disableEdit={!isJobEditable}
        />
      )}
      {isImagePreviewOpen && (
        <ImagePreview
          onClose={() => setIsImagePreviewOpen(false)}
          images={[
            {
              id: selectedExpense?.receiptUrl,
              title: selectedExpense?.title,
              url: selectedExpense?.receiptUrl,
              urlLink: selectedExpense?.receiptUrl
            }
          ]}
          selectedIndex={0}
        />
      )}
      {isDeleteConfirmationOpen && (
        <GenericDeleteModal
          onPrimaryBtnClick={() => handleDeleteExpense(selectedExpense)}
          onSecondaryBtnClick={handleCloseDeleteConfirmation}
          showModal={isDeleteConfirmationOpen}
          isActionInProgress={isDeletingExpense}
          primaryBtnId="delete-expense-button"
          secondaryBtnId="cancel-delete-expense-button"
          title="Delete Expense"
          subTitle="Are you sure you want to delete this expense?"
          primaryBtnLabel="Delete"
          secondaryBtnLabel="Cancel"
        />
      )}
    </>
  );
};

export default ExpensesTable;
